import {
  Alert,
  App as AntApp,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CopyOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { api, type ApiError } from "../../services/api";
import { useAuth } from "../../services/auth";
import { ChannelServiceControlCard } from "../../components/ChannelServiceControlCard";

type ModelTier = "flash" | "pro";

interface QqGroupConfig {
  openid: string;
  enabled: boolean;
  note: string;
}

interface QqAnswerConfig {
  modelTier: ModelTier;
  shortAnswerTargetChars: number;
  historyRoundLimit: number;
  skills: { time: boolean; weather: boolean; webSearch: boolean; musicCatalog: boolean };
  dailyQuota: { weatherPerUser: number; webSearchPerUser: number };
}

interface QqRepeatConfig {
  enabled: boolean;
  maxChars: number;
  requireDistinctSenders: boolean;
}

interface QqRuntimeConfig {
  version: 1;
  repliesEnabled: boolean;
  groups: QqGroupConfig[];
  answer: QqAnswerConfig;
  repeat: QqRepeatConfig;
}

interface QqHealth {
  enabled: boolean;
  serviceEnabled: boolean;
  state: "disabled" | "down" | "connecting" | "up";
  connected: boolean;
  repliesEnabled: boolean;
  repeatEnabled: boolean;
  allowedGroupCount: number;
  observedGroupCount: number;
  groupMessages: number;
  allowedGroupMessages: number;
  questionReplies: number;
  repeatMatches: number;
  sentMessages: number;
  fullMessageEventsObserved: boolean;
  reconnectAttempt: number;
  lastReadyAt: string | null;
  lastResumedAt: string | null;
  lastEventAt: string | null;
  lastAllowedGroupMessageAt: string | null;
  lastSentAt: string | null;
  lastError: string | null;
}

interface QqCostWindow {
  since: string;
  turns: number;
  delivered: number;
  brainRuns: number;
  costCny: number;
  averageCostCny: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  averageLatencyMs: number;
  models: Array<{ model: string; calls: number; costCny: number }>;
}

interface QqSnapshot {
  health: QqHealth;
  configuration: QqRuntimeConfig & {
    source: "database" | "environment";
    updatedAt: string | null;
  };
  groups: Array<QqGroupConfig & {
    configured: boolean;
    observed: boolean;
    messageCount: number;
    replyCount: number;
    lastMessageAt: string | null;
    lastParticipantName: string | null;
    lastUserText: string | null;
  }>;
  credentials: {
    qqAppIdConfigured: boolean;
    qqClientSecretConfigured: boolean;
    bailianConfigured: boolean;
    weatherConfigured: boolean;
  };
  modelOptions: Array<{
    tier: ModelTier;
    capability: string;
    model: string;
    inputCnyPerMillion: number;
    cachedInputCnyPerMillion: number;
    outputCnyPerMillion: number;
  }>;
  costs: { today: QqCostWindow; month: QqCostWindow };
  audit: Array<{
    id: string;
    operatorId: string;
    operatorName: string | null;
    action: string;
    reason: string | null;
    requestId: string | null;
    createdAt: string;
    restorable: boolean;
  }>;
}

interface QqMessageLogPage {
  items: Array<{
    id: string;
    groupOpenid: string;
    groupNote: string;
    participantKey: string;
    participantLabel: string;
    participantName: string | null;
    userText: string;
    assistantText: string;
    delivered: boolean;
    status: string | null;
    model: string | null;
    costCny: number;
    latencyMs: number | null;
    createdAt: string;
  }>;
  participants: Array<{
    groupOpenid: string;
    groupNote: string;
    participantKey: string;
    participantLabel: string;
    participantName: string | null;
    messageCount: number;
    lastMessageAt: string | null;
  }>;
  pagination: { page: number; pageSize: number; total: number };
}

type QqBindingStatus =
  | "awaiting_submission"
  | "pending_confirmation"
  | "active"
  | "rejected"
  | "expired"
  | "revoked";

interface QqBindingPage {
  items: Array<{
    id: string;
    userId: string;
    userDisplayName: string | null;
    accountId: string;
    accountDisplayName: string | null;
    actorDisplayName: string | null;
    identityLabel: string | null;
    historicalIdentityCount: number;
    scopeLabel: string | null;
    status: QqBindingStatus;
    expiresAt: string;
    submittedAt: string | null;
    confirmedAt: string | null;
    rejectedAt: string | null;
    revokedAt: string | null;
    effectiveFrom: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  pagination: { page: number; pageSize: number; total: number };
}

type ConfigurationPatch = Partial<
  Pick<QqRuntimeConfig, "repliesEnabled" | "groups" | "answer" | "repeat">
>;

const QUERY_KEY = ["qqOfficialAdmin"] as const;
const OPENID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;
const BINDING_STATUS_OPTIONS: Array<{
  value: QqBindingStatus;
  label: string;
  color: string;
}> = [
  { value: "awaiting_submission", label: "等待 QQ 提交", color: "processing" },
  { value: "pending_confirmation", label: "等待站内确认", color: "warning" },
  { value: "active", label: "已绑定", color: "success" },
  { value: "rejected", label: "已拒绝", color: "error" },
  { value: "expired", label: "已过期", color: "default" },
  { value: "revoked", label: "已解绑", color: "default" },
];

function runtimeDraft(snapshot: QqSnapshot): QqRuntimeConfig {
  const { version, repliesEnabled, groups, answer, repeat } = snapshot.configuration;
  return structuredClone({ version, repliesEnabled, groups, answer, repeat });
}

function timeText(value: string | null): string {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—";
}

function bindingStatusTag(status: QqBindingStatus) {
  const option = BINDING_STATUS_OPTIONS.find((candidate) => candidate.value === status);
  return <Tag color={option?.color}>{option?.label ?? status}</Tag>;
}

function groupShortId(openid: string): string {
  return openid.length <= 12
    ? openid
    : `${openid.slice(0, 4)}…${openid.slice(-8)}`;
}

function groupDisplayName(group: Pick<QqGroupConfig, "openid" | "note">, index: number): string {
  return group.note.trim() || `群 ${index + 1} · ${groupShortId(group.openid)}`;
}

function stateLabel(health: QqHealth): { status: "success" | "warning" | "error" | "default"; text: string } {
  if (!health.enabled) return { status: "default", text: "未配置凭据" };
  if (!health.serviceEnabled) return { status: "default", text: "后端服务已关闭" };
  if (health.connected) {
    return health.repliesEnabled
      ? { status: "success", text: "在线回复中" }
      : { status: "warning", text: "在线但暂停回复" };
  }
  if (health.state === "connecting") return { status: "warning", text: "连接中" };
  return { status: "error", text: "离线" };
}

export function QqBotPage() {
  const { can } = useAuth();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const editable = can("channel.write");
  const [draft, setDraft] = useState<QqRuntimeConfig | null>(null);
  const [manualOpenid, setManualOpenid] = useState("");
  const [rollbackTarget, setRollbackTarget] = useState<QqSnapshot["audit"][number] | null>(null);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<QqSnapshot>("/api/v1/admin/channels/qq"),
  });

  useEffect(() => {
    if (query.data) setDraft(runtimeDraft(query.data));
  }, [query.data]);

  const save = useMutation({
    mutationFn: ({ patch, reason }: { patch: ConfigurationPatch; reason: string }) =>
      api.patch<QqSnapshot>("/api/v1/admin/channels/qq/configuration", {
        expectedUpdatedAt: query.data?.configuration.updatedAt ?? null,
        reason,
        ...patch,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next);
      setDraft(runtimeDraft(next));
      message.success("QQ 机器人配置已即时生效");
    },
    onError: (error: Error) => showApiError(message.error, error),
  });

  const rollback = useMutation({
    mutationFn: ({ auditId, reason }: { auditId: string; reason: string }) =>
      api.post<QqSnapshot>(
        `/api/v1/admin/channels/qq/configuration/rollback/${auditId}`,
        {
          expectedUpdatedAt: query.data?.configuration.updatedAt ?? null,
          reason,
        },
      ),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next);
      setDraft(runtimeDraft(next));
      setRollbackTarget(null);
      message.success("已恢复到所选配置之前的版本");
    },
    onError: (error: Error) => showApiError(message.error, error),
  });

  const observedPending = useMemo(
    () => query.data?.groups.filter((group) => !group.configured) ?? [],
    [query.data?.groups],
  );

  if (query.error) {
    return <Alert type="error" showIcon message={(query.error as Error).message} />;
  }
  if (!query.data || !draft) {
    return <Card loading title="QQ 机器人" />;
  }

  const snapshot = query.data;
  const state = stateLabel(snapshot.health);
  const patchDraft = (next: Partial<QqRuntimeConfig>) =>
    setDraft((current) => (current ? { ...current, ...next } : current));
  const addGroup = (openid: string) => {
    const normalized = openid.trim();
    if (!OPENID_PATTERN.test(normalized)) {
      message.error("请输入合法的群 OpenID");
      return;
    }
    if (draft.groups.some((group) => group.openid === normalized)) {
      message.info("这个群已经在配置中");
      return;
    }
    patchDraft({
      groups: [...draft.groups, { openid: normalized, enabled: true, note: "" }],
    });
    setManualOpenid("");
  };

  const tabItems = [
    {
      key: "status",
      label: "运行状态",
      children: (
        <StatusTab
          snapshot={snapshot}
          draft={draft}
          editable={editable}
          state={state}
          refreshing={query.isFetching}
          onRefresh={() => void query.refetch()}
          onRepliesEnabled={(repliesEnabled) => patchDraft({ repliesEnabled })}
          onSave={(reason) => save.mutateAsync({ patch: { repliesEnabled: draft.repliesEnabled }, reason })}
          onServiceChanged={() => query.refetch()}
          saving={save.isPending}
        />
      ),
    },
    {
      key: "groups",
      label: `群管理（${draft.groups.length}）`,
      children: (
        <GroupsTab
          groups={draft.groups}
          stats={snapshot.groups}
          pending={observedPending}
          manualOpenid={manualOpenid}
          editable={editable}
          saving={save.isPending}
          onManualOpenid={setManualOpenid}
          onAdd={addGroup}
          onGroups={(groups) => patchDraft({ groups })}
          onSave={(reason) => save.mutateAsync({ patch: { groups: draft.groups }, reason })}
        />
      ),
    },
    {
      key: "bindings",
      label: <span><LinkOutlined /> 绑定身份</span>,
      children: <BindingsTab />,
    },
    {
      key: "messages",
      label: "消息日志",
      children: <MessageLogsTab groups={snapshot.groups} />,
    },
    {
      key: "answer",
      label: "对话策略",
      children: (
        <AnswerTab
          answer={draft.answer}
          modelOptions={snapshot.modelOptions}
          credentials={snapshot.credentials}
          editable={editable}
          saving={save.isPending}
          onAnswer={(answer) => patchDraft({ answer })}
          onSave={(reason) => save.mutateAsync({ patch: { answer: draft.answer }, reason })}
        />
      ),
    },
    {
      key: "repeat",
      label: "复读",
      children: (
        <RepeatTab
          repeat={draft.repeat}
          fullMessageEventsObserved={snapshot.health.fullMessageEventsObserved}
          editable={editable}
          saving={save.isPending}
          onRepeat={(repeat) => patchDraft({ repeat })}
          onSave={(reason) => save.mutateAsync({ patch: { repeat: draft.repeat }, reason })}
        />
      ),
    },
    {
      key: "cost",
      label: "费用",
      children: <CostTab costs={snapshot.costs} />,
    },
    {
      key: "audit",
      label: "变更记录",
      children: (
        <AuditTab
          rows={snapshot.audit}
          editable={editable}
          onRollback={setRollbackTarget}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      {!editable ? (
        <Alert type="warning" showIcon message="你没有 channel.write 权限，只能查看配置" />
      ) : null}
      <Card styles={{ body: { paddingTop: 4 } }}>
        <Tabs items={tabItems} destroyInactiveTabPane={false} />
      </Card>
      {rollbackTarget ? (
        <RollbackModal
          row={rollbackTarget}
          loading={rollback.isPending}
          onClose={() => setRollbackTarget(null)}
          onSubmit={(reason) => rollback.mutate({ auditId: rollbackTarget.id, reason })}
        />
      ) : null}
    </Space>
  );
}

function StatusTab({
  snapshot,
  draft,
  editable,
  state,
  refreshing,
  saving,
  onRefresh,
  onRepliesEnabled,
  onSave,
  onServiceChanged,
}: {
  snapshot: QqSnapshot;
  draft: QqRuntimeConfig;
  editable: boolean;
  state: ReturnType<typeof stateLabel>;
  refreshing: boolean;
  saving: boolean;
  onRefresh: () => void;
  onRepliesEnabled: (enabled: boolean) => void;
  onSave: (reason: string) => Promise<unknown>;
  onServiceChanged: () => Promise<unknown>;
}) {
  const health = snapshot.health;
  const credentials = snapshot.credentials;
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card size="small">
        <Space direction="vertical" size={4}>
          <Space size={12} wrap>
            <Typography.Title level={4} style={{ margin: 0 }}>
              QQ 机器人
            </Typography.Title>
            <Badge status={state.status} text={state.text} />
            <Tag color={snapshot.configuration.source === "database" ? "blue" : "default"}>
              {snapshot.configuration.source === "database" ? "后台配置" : "部署默认值"}
            </Tag>
          </Space>
          <Typography.Text type="secondary">
            这里只控制 QQ 公开群聊。模型、历史和能力开关不会影响小程序/H5 私聊。
          </Typography.Text>
        </Space>
      </Card>
      <ChannelServiceControlCard
        queryKey="qq-official-service-control"
        endpoint="/api/v1/admin/channels/qq/service-control"
        title="QQ 机器人后端服务"
        enabledMessage="当前环境允许连接 QQ Gateway 并响应群聊活动"
        disabledMessage="当前环境不会连接 QQ Gateway，也不会响应 QQ 活动"
        description="关闭会立即断开当前 Gateway；已经开始生成但尚未发送的回复也会在发送前被拦截。回复策略开关只暂停回复但保持连接，本开关则停止整个后端服务。"
        requirementLabels={{
          credentialsConfigured: "QQ AppID / AppSecret",
          characterReady: "机器人角色",
        }}
        onChanged={onServiceChanged}
      />
      <Row gutter={[12, 12]}>
        <Col xs={24} md={6}><Card size="small"><Statistic title="连接" value={state.text} /></Card></Col>
        <Col xs={24} md={6}><Card size="small"><Statistic title="白名单群" value={health.allowedGroupCount} /></Card></Col>
        <Col xs={24} md={6}><Card size="small"><Statistic title="已回答" value={health.questionReplies} /></Card></Col>
        <Col xs={24} md={6}><Card size="small"><Statistic title="已发送" value={health.sentMessages} /></Card></Col>
      </Row>
      {health.lastError ? <Alert type="error" showIcon message="最近错误" description={health.lastError} /> : null}
      <Card
        size="small"
        title="链路状态"
        extra={<Button icon={<ReloadOutlined />} loading={refreshing} onClick={onRefresh}>刷新</Button>}
      >
        <Descriptions size="small" column={{ xs: 1, md: 2, lg: 3 }}>
          <Descriptions.Item label="最近 READY">{timeText(health.lastReadyAt)}</Descriptions.Item>
          <Descriptions.Item label="最近 RESUME">{timeText(health.lastResumedAt)}</Descriptions.Item>
          <Descriptions.Item label="最近事件">{timeText(health.lastEventAt)}</Descriptions.Item>
          <Descriptions.Item label="白名单群最近消息">{timeText(health.lastAllowedGroupMessageAt)}</Descriptions.Item>
          <Descriptions.Item label="最近发送">{timeText(health.lastSentAt)}</Descriptions.Item>
          <Descriptions.Item label="重连次数">{health.reconnectAttempt}</Descriptions.Item>
          <Descriptions.Item label="群消息事件">{health.groupMessages}</Descriptions.Item>
          <Descriptions.Item label="白名单消息">{health.allowedGroupMessages}</Descriptions.Item>
          <Descriptions.Item label="复读命中">{health.repeatMatches}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card size="small" title="服务端凭据（不显示明文）">
        <Space wrap>
          <CredentialTag label="QQ AppID" configured={credentials.qqAppIdConfigured} />
          <CredentialTag label="QQ AppSecret" configured={credentials.qqClientSecretConfigured} />
          <CredentialTag label="百炼" configured={credentials.bailianConfigured} />
        </Space>
      </Card>
      <Card size="small" title="回复总开关">
        <Space direction="vertical" style={{ width: "100%" }}>
          <Space>
            <Switch checked={draft.repliesEnabled} disabled={!editable} onChange={onRepliesEnabled} />
            <Typography.Text>{draft.repliesEnabled ? "允许白名单群回复" : "保持连接，但暂停所有回复"}</Typography.Text>
          </Space>
          <SaveBar editable={editable} loading={saving} onSave={onSave} placeholder="为什么暂停或恢复 QQ 回复" />
        </Space>
      </Card>
    </Space>
  );
}

function CredentialTag({ label, configured }: { label: string; configured: boolean }) {
  return (
    <Tag color={configured ? "success" : "error"} icon={configured ? <CheckCircleOutlined /> : undefined}>
      {label}：{configured ? "已配置" : "未配置"}
    </Tag>
  );
}

function GroupsTab({
  groups,
  stats,
  pending,
  manualOpenid,
  editable,
  saving,
  onManualOpenid,
  onAdd,
  onGroups,
  onSave,
}: {
  groups: QqGroupConfig[];
  stats: QqSnapshot["groups"];
  pending: QqSnapshot["groups"];
  manualOpenid: string;
  editable: boolean;
  saving: boolean;
  onManualOpenid: (value: string) => void;
  onAdd: (openid: string) => void;
  onGroups: (groups: QqGroupConfig[]) => void;
  onSave: (reason: string) => Promise<unknown>;
}) {
  const update = (openid: string, patch: Partial<QqGroupConfig>) =>
    onGroups(groups.map((group) => (group.openid === openid ? { ...group, ...patch } : group)));
  const statsByOpenid = new Map(stats.map((group) => [group.openid, group]));
  return (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="QQ 官方接口不提供真实群名，请在“群名称 / 标识”里填写便于识别的名称；完整 OpenID 仍可一键复制。"
      />
      {pending.length ? (
        <Card size="small" title="刚发现、尚未加入白名单的群">
          <Space wrap>
            {pending.map((group) => (
              <Button key={group.openid} disabled={!editable} onClick={() => onAdd(group.openid)}>
                加入新发现群 · {groupShortId(group.openid)}
              </Button>
            ))}
          </Space>
        </Card>
      ) : null}
      <Space.Compact style={{ width: "100%", maxWidth: 720 }}>
        <Input value={manualOpenid} disabled={!editable} placeholder="手动输入群 OpenID" onChange={(event) => onManualOpenid(event.target.value)} onPressEnter={() => onAdd(manualOpenid)} />
        <Button type="primary" icon={<PlusOutlined />} disabled={!editable} onClick={() => onAdd(manualOpenid)}>添加</Button>
      </Space.Compact>
      <SaveBar
        editable={editable}
        loading={saving}
        onSave={onSave}
        placeholder="例如：填写真实群名，方便运营识别"
        buttonText="保存群配置"
      />
      <Table<QqGroupConfig>
        size="small"
        rowKey="openid"
        pagination={false}
        dataSource={groups}
        scroll={{ x: 820 }}
        columns={[
          {
            title: "群名称 / 标识",
            dataIndex: "note",
            width: 320,
            render: (value: string, row, index) => (
              <Input
                value={value}
                maxLength={80}
                disabled={!editable}
                addonBefore={`群 ${index + 1}`}
                placeholder={`请填写群名，例如：缈缈测试群`}
                onChange={(event) => update(row.openid, { note: event.target.value })}
              />
            ),
          },
          {
            title: "群 OpenID",
            dataIndex: "openid",
            width: 230,
            render: (value: string) => (
              <Typography.Text
                copyable={{ text: value, icon: <CopyOutlined /> }}
                code
                title={value}
              >
                {groupShortId(value)}
              </Typography.Text>
            ),
          },
          {
            title: "已记录消息",
            width: 170,
            render: (_, row) => {
              const stat = statsByOpenid.get(row.openid);
              const questions = stat?.messageCount ?? 0;
              const replies = stat?.replyCount ?? 0;
              return (
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{questions + replies} 条</Typography.Text>
                  <Typography.Text type="secondary">
                    {questions} 条提问 / {replies} 条回复
                  </Typography.Text>
                </Space>
              );
            },
          },
          {
            title: "最近消息",
            width: 230,
            render: (_, row) => {
              const stat = statsByOpenid.get(row.openid);
              if (!stat?.lastMessageAt) return "—";
              return (
                <Space direction="vertical" size={0} style={{ maxWidth: 220 }}>
                  <Typography.Text ellipsis title={stat.lastUserText ?? undefined}>
                    {stat.lastParticipantName ?? "未提供 QQ 昵称"}：{stat.lastUserText ?? "—"}
                  </Typography.Text>
                  <Typography.Text type="secondary">{timeText(stat.lastMessageAt)}</Typography.Text>
                </Space>
              );
            },
          },
          {
            title: "允许回复",
            dataIndex: "enabled",
            width: 110,
            render: (value: boolean, row) => <Switch checked={value} disabled={!editable} onChange={(enabled) => update(row.openid, { enabled })} />,
          },
          {
            title: "操作",
            width: 90,
            render: (_, row) => (
              <Popconfirm title="移出配置？" description="保存后机器人将不再回复这个群。" onConfirm={() => onGroups(groups.filter((group) => group.openid !== row.openid))}>
                <Button type="link" danger disabled={!editable}>移除</Button>
              </Popconfirm>
            ),
          },
        ]}
      />
    </Space>
  );
}

function BindingsTab() {
  const [status, setStatus] = useState<QqBindingStatus | undefined>();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const query = useQuery({
    queryKey: ["qqOfficialBindings", status ?? "all", search ?? "all", page, pageSize],
    queryFn: () =>
      api.get<QqBindingPage>("/api/v1/admin/channels/qq/bindings", {
        status,
        search,
        page,
        pageSize,
      }),
  });
  const applySearch = (value: string) => {
    setSearch(value.trim() || undefined);
    setPage(1);
  };

  return (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="这里展示用户主动发起并在 QQ 完成验证的身份绑定。平台原始 OpenID 不会返回后台；绑定确认与解除仍由用户本人操作。"
      />
      <Space wrap>
        <Select<QqBindingStatus>
          allowClear
          style={{ width: 190 }}
          placeholder="全部绑定状态"
          value={status}
          options={BINDING_STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <Input.Search
          allowClear
          style={{ width: 320 }}
          value={searchInput}
          placeholder="搜索 QQ 昵称、站内昵称或完整 User ID"
          onChange={(event) => {
            const value = event.target.value;
            setSearchInput(value);
            if (!value) applySearch("");
          }}
          onSearch={applySearch}
        />
        <Tag color="blue">共 {query.data?.pagination.total ?? 0} 条绑定记录</Tag>
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => void query.refetch()}>
          刷新
        </Button>
      </Space>
      {query.error ? <Alert type="error" showIcon message={(query.error as Error).message} /> : null}
      <Table<QqBindingPage["items"][number]>
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        scroll={{ x: 1080 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.pagination.total ?? 0,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条绑定记录`,
          onChange: setPage,
        }}
        columns={[
          {
            title: "创建时间",
            dataIndex: "createdAt",
            width: 165,
            render: timeText,
          },
          {
            title: "站内用户",
            width: 210,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.userDisplayName || "未设置昵称"}</Typography.Text>
                <Typography.Text type="secondary" copyable={{ text: row.userId }}>
                  {row.userId.slice(0, 12)}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "QQ 身份",
            width: 220,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.actorDisplayName || "未提供 QQ 昵称"}</Typography.Text>
                <Typography.Text type="secondary">
                  {row.identityLabel || "等待 QQ 提交"}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "验证位置",
            dataIndex: "scopeLabel",
            width: 180,
            render: (value: string | null) => value ?? "尚未提交",
          },
          {
            title: "状态",
            dataIndex: "status",
            width: 130,
            render: bindingStatusTag,
          },
          {
            title: "确认时间",
            dataIndex: "confirmedAt",
            width: 165,
            render: timeText,
          },
        ]}
        expandable={{
          expandedRowRender: (row) => (
            <Descriptions bordered size="small" column={{ xs: 1, md: 2, lg: 3 }}>
              <Descriptions.Item label="绑定 ID">
                <Typography.Text copyable={{ text: row.id }}>{row.id}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="User ID">
                <Typography.Text copyable={{ text: row.userId }}>{row.userId}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="机器人账号">{row.accountDisplayName ?? row.accountId}</Descriptions.Item>
              <Descriptions.Item label="提交时间">{timeText(row.submittedAt)}</Descriptions.Item>
              <Descriptions.Item label="生效时间">{timeText(row.effectiveFrom)}</Descriptions.Item>
              <Descriptions.Item label="过期时间">{timeText(row.expiresAt)}</Descriptions.Item>
              <Descriptions.Item label="拒绝时间">{timeText(row.rejectedAt)}</Descriptions.Item>
              <Descriptions.Item label="解绑时间">{timeText(row.revokedAt)}</Descriptions.Item>
              <Descriptions.Item label="历史身份键">{row.historicalIdentityCount} 个不可逆摘要</Descriptions.Item>
            </Descriptions>
          ),
        }}
      />
    </Space>
  );
}

function MessageLogsTab({ groups }: { groups: QqSnapshot["groups"] }) {
  const [groupOpenid, setGroupOpenid] = useState<string | undefined>();
  const [participantKey, setParticipantKey] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const query = useQuery({
    queryKey: [
      "qqOfficialMessages",
      groupOpenid ?? "all",
      participantKey ?? "all-members",
      page,
      pageSize,
    ],
    queryFn: () =>
      api.get<QqMessageLogPage>("/api/v1/admin/channels/qq/messages", {
        groupOpenid,
        participantKey,
        page,
        pageSize,
      }),
  });
  const groupIndex = new Map(groups.map((group, index) => [group.openid, index]));

  return (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="这里显示机器人已经接收并写入数据库的问答。QQ 官方当前只返回发言者的 QQ 昵称，不返回群名片；未触发机器人的普通群消息不会记录。"
      />
      <Space wrap>
        <Select
          allowClear
          style={{ width: 280 }}
          placeholder="全部群"
          value={groupOpenid}
          options={groups.map((group, index) => ({
            value: group.openid,
            label: groupDisplayName(group, index),
          }))}
          onChange={(value) => {
            setGroupOpenid(value);
            setParticipantKey(undefined);
            setPage(1);
          }}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 360 }}
          placeholder="全部群友"
          value={participantKey}
          options={(query.data?.participants ?? []).map((participant) => {
            const group = groups.find(
              (candidate) => candidate.openid === participant.groupOpenid,
            );
            const index = groupIndex.get(participant.groupOpenid) ?? 0;
            const groupName = group
              ? groupDisplayName(group, index)
              : groupShortId(participant.groupOpenid);
            return {
              value: participant.participantKey,
              label: `${participant.participantName ?? "未提供 QQ 昵称"} · ${participant.participantLabel} · ${participant.messageCount} 条${groupOpenid ? "" : ` · ${groupName}`}`,
            };
          })}
          onChange={(value) => {
            setParticipantKey(value);
            setPage(1);
          }}
        />
        <Tag color="blue">已识别 {query.data?.participants.length ?? 0} 位群友</Tag>
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => void query.refetch()}>
          刷新
        </Button>
      </Space>
      {query.error ? <Alert type="error" showIcon message={(query.error as Error).message} /> : null}
      <Table<QqMessageLogPage["items"][number]>
        size="small"
        rowKey="id"
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        scroll={{ x: 1180 }}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.pagination.total ?? 0,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 轮问答`,
          onChange: setPage,
        }}
        columns={[
          {
            title: "时间",
            dataIndex: "createdAt",
            width: 165,
            render: timeText,
          },
          {
            title: "群",
            width: 190,
            render: (_, row) => {
              const index = groupIndex.get(row.groupOpenid) ?? 0;
              return groupDisplayName(
                { openid: row.groupOpenid, note: row.groupNote },
                index,
              );
            },
          },
          {
            title: "QQ 昵称 / 群友标识",
            width: 210,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.participantName || "未提供 QQ 昵称"}</Typography.Text>
                <Typography.Text type="secondary" copyable={{ text: row.participantLabel }}>
                  {row.participantLabel}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "群友提问",
            dataIndex: "userText",
            width: 260,
            render: (value: string) => (
              <Typography.Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2 }} title={value}>
                {value}
              </Typography.Paragraph>
            ),
          },
          {
            title: "缈缈回复",
            dataIndex: "assistantText",
            width: 300,
            render: (value: string) => (
              <Typography.Paragraph style={{ margin: 0 }} ellipsis={{ rows: 2 }} title={value}>
                {value}
              </Typography.Paragraph>
            ),
          },
          {
            title: "状态",
            width: 100,
            render: (_, row) => (
              <Tag color={row.delivered ? "success" : "error"}>
                {row.delivered ? "已发送" : "未发送"}
              </Tag>
            ),
          },
          {
            title: "模型 / 费用",
            width: 190,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.model ?? "—"}</Typography.Text>
                <Typography.Text type="secondary">
                  ¥{row.costCny.toFixed(6)} · {row.latencyMs ?? 0} ms
                </Typography.Text>
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}

function AnswerTab({
  answer,
  modelOptions,
  credentials,
  editable,
  saving,
  onAnswer,
  onSave,
}: {
  answer: QqAnswerConfig;
  modelOptions: QqSnapshot["modelOptions"];
  credentials: QqSnapshot["credentials"];
  editable: boolean;
  saving: boolean;
  onAnswer: (answer: QqAnswerConfig) => void;
  onSave: (reason: string) => Promise<unknown>;
}) {
  const patch = (next: Partial<QqAnswerConfig>) => onAnswer({ ...answer, ...next });
  const selected = modelOptions.find((option) => option.tier === answer.modelTier);
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert type="info" showIcon message="回复字数限制通过提示词约束，不会在发送前硬截断。历史按“群 + 群友”隔离。" />
      <Card size="small" title="回答模型与上下文">
        <Row gutter={[20, 16]}>
          <Col xs={24} md={8}>
            <Typography.Text strong>回答模型</Typography.Text>
            <Select
              style={{ width: "100%", marginTop: 8 }}
              value={answer.modelTier}
              disabled={!editable}
              options={modelOptions.map((option) => ({
                value: option.tier,
                label: `${option.tier === "flash" ? "Flash" : "Pro"} · ${option.model}`,
              }))}
              onChange={(modelTier) => patch({ modelTier })}
            />
            {selected ? (
              <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                输入 ¥{selected.inputCnyPerMillion}/百万 Token，缓存命中 ¥{selected.cachedInputCnyPerMillion}/百万，输出 ¥{selected.outputCnyPerMillion}/百万。
              </Typography.Paragraph>
            ) : null}
          </Col>
          <Col xs={24} md={8}>
            <Typography.Text strong>回复字数限制（字符）</Typography.Text>
            <div><InputNumber style={{ width: "100%", marginTop: 8 }} min={40} max={500} value={answer.shortAnswerTargetChars} disabled={!editable} onChange={(value) => patch({ shortAnswerTargetChars: value ?? answer.shortAnswerTargetChars })} /></div>
          </Col>
          <Col xs={24} md={8}>
            <Typography.Text strong>每位群友历史（轮）</Typography.Text>
            <div><InputNumber style={{ width: "100%", marginTop: 8 }} min={0} max={12} value={answer.historyRoundLimit} disabled={!editable} onChange={(value) => patch({ historyRoundLimit: value ?? 0 })} /></div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>0 表示不带历史；不会看到其他群友的上下文。</Typography.Text>
          </Col>
        </Row>
      </Card>
      <Card size="small" title="公开群能力">
        <Descriptions size="small" bordered column={1}>
          <Descriptions.Item label="当前时间">
            <Switch checked={answer.skills.time} disabled={!editable} onChange={(time) => patch({ skills: { ...answer.skills, time } })} />
          </Descriptions.Item>
          <Descriptions.Item label="天气查询">
            <Space wrap>
              <Switch checked={false} disabled />
              <Tag color="default">专用天气已停用；可用联网搜索查询天气</Tag>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="联网搜索">
            <Space wrap>
              <Switch checked={answer.skills.webSearch} disabled={!editable} onChange={(webSearch) => patch({ skills: { ...answer.skills, webSearch } })} />
              <InputNumber addonBefore="每人每天" addonAfter="次" min={0} max={1000} value={answer.dailyQuota.webSearchPerUser} disabled={!editable || !answer.skills.webSearch} onChange={(value) => patch({ dailyQuota: { ...answer.dailyQuota, webSearchPerUser: value ?? 0 } })} />
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="角色曲库查询">
            <Switch checked={answer.skills.musicCatalog} disabled={!editable} onChange={(musicCatalog) => patch({ skills: { ...answer.skills, musicCatalog } })} />
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <SaveBar editable={editable} loading={saving} onSave={onSave} placeholder="例如：试运行 Flash，并把历史调整为 6 轮" />
    </Space>
  );
}

function RepeatTab({
  repeat,
  fullMessageEventsObserved,
  editable,
  saving,
  onRepeat,
  onSave,
}: {
  repeat: QqRepeatConfig;
  fullMessageEventsObserved: boolean;
  editable: boolean;
  saving: boolean;
  onRepeat: (repeat: QqRepeatConfig) => void;
  onSave: (reason: string) => Promise<unknown>;
}) {
  const patch = (next: Partial<QqRepeatConfig>) => onRepeat({ ...repeat, ...next });
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {!fullMessageEventsObserved ? (
        <Alert type="warning" showIcon message="尚未观察到普通群消息事件" description="即使打开复读，QQ 平台没有下发 GROUP_MESSAGE_CREATE 时也不会生效；需要在群里的机器人权限中允许获取全部群消息。" />
      ) : (
        <Alert type="success" showIcon message="已观察到普通群消息事件，平台权限满足复读条件" />
      )}
      <Card size="small">
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="启用复读">
            <Switch checked={repeat.enabled} disabled={!editable} onChange={(enabled) => patch({ enabled })} />
          </Descriptions.Item>
          <Descriptions.Item label="最大文本长度">
            <InputNumber min={1} max={500} value={repeat.maxChars} disabled={!editable || !repeat.enabled} addonAfter="字符" onChange={(value) => patch({ maxChars: value ?? 80 })} />
          </Descriptions.Item>
          <Descriptions.Item label="要求不同群友连续发送">
            <Switch checked={repeat.requireDistinctSenders} disabled={!editable || !repeat.enabled} onChange={(requireDistinctSenders) => patch({ requireDistinctSenders })} />
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Typography.Paragraph type="secondary">
        只复读纯文本；命令、链接、图片、表情会跳过。同一句不会连续复读。
      </Typography.Paragraph>
      <SaveBar editable={editable} loading={saving} onSave={onSave} placeholder="例如：在已授权全部群消息后开启复读" />
    </Space>
  );
}

function CostTab({ costs }: { costs: QqSnapshot["costs"] }) {
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert type="info" showIcon message="这里统计 QQ 问答复用的 BrainRun、模型与工具费用；服务器固定成本不包含在内。" />
      <Row gutter={[12, 12]}>
        <Col xs={24} md={6}><Card size="small"><Statistic title="今日费用" prefix="¥" value={costs.today.costCny} precision={4} /></Card></Col>
        <Col xs={24} md={6}><Card size="small"><Statistic title="今日问答" value={costs.today.turns} /></Card></Col>
        <Col xs={24} md={6}><Card size="small"><Statistic title="本月费用" prefix="¥" value={costs.month.costCny} precision={4} /></Card></Col>
        <Col xs={24} md={6}><Card size="small"><Statistic title="本月单问平均" prefix="¥" value={costs.month.averageCostCny} precision={5} /></Card></Col>
      </Row>
      <Descriptions bordered size="small" column={{ xs: 1, md: 2, lg: 4 }}>
        <Descriptions.Item label="本月问答">{costs.month.turns}</Descriptions.Item>
        <Descriptions.Item label="成功发送">{costs.month.delivered}</Descriptions.Item>
        <Descriptions.Item label="平均耗时">{costs.month.averageLatencyMs} ms</Descriptions.Item>
        <Descriptions.Item label="BrainRun">{costs.month.brainRuns}</Descriptions.Item>
        <Descriptions.Item label="输入 Token">{costs.month.inputTokens.toLocaleString()}</Descriptions.Item>
        <Descriptions.Item label="缓存 Token">{costs.month.cachedTokens.toLocaleString()}</Descriptions.Item>
        <Descriptions.Item label="输出 Token">{costs.month.outputTokens.toLocaleString()}</Descriptions.Item>
      </Descriptions>
      <Table<QqCostWindow["models"][number]>
        size="small"
        rowKey="model"
        pagination={false}
        dataSource={costs.month.models}
        columns={[
          { title: "本月模型", dataIndex: "model" },
          { title: "调用次数", dataIndex: "calls", width: 140, align: "right" },
          { title: "费用", dataIndex: "costCny", width: 160, align: "right", render: (value: number) => `¥${value.toFixed(4)}` },
        ]}
      />
    </Space>
  );
}

function AuditTab({
  rows,
  editable,
  onRollback,
}: {
  rows: QqSnapshot["audit"];
  editable: boolean;
  onRollback: (row: QqSnapshot["audit"][number]) => void;
}) {
  return (
    <Table<QqSnapshot["audit"][number]>
      size="small"
      rowKey="id"
      pagination={{ pageSize: 10, hideOnSinglePage: true }}
      dataSource={rows}
      columns={[
        { title: "时间", dataIndex: "createdAt", width: 180, render: timeText },
        { title: "管理员", dataIndex: "operatorName", width: 140, render: (value: string | null, row) => value ?? row.operatorId.slice(0, 8) },
        { title: "动作", dataIndex: "action", width: 230, render: (value: string) => value.includes("rollback") ? "回滚配置" : "更新配置" },
        { title: "理由", dataIndex: "reason", render: (value: string | null) => value || "—" },
        {
          title: "操作",
          width: 110,
          render: (_, row) => (
            <Button type="link" icon={<RollbackOutlined />} disabled={!editable || !row.restorable} onClick={() => onRollback(row)}>
              回到此前
            </Button>
          ),
        },
      ]}
    />
  );
}

function SaveBar({
  editable,
  loading,
  placeholder,
  buttonText = "保存并立即生效",
  onSave,
}: {
  editable: boolean;
  loading: boolean;
  placeholder: string;
  buttonText?: string;
  onSave: (reason: string) => Promise<unknown>;
}) {
  const [reason, setReason] = useState("");
  const submit = async () => {
    const normalized = reason.trim();
    if (!normalized) return;
    await onSave(normalized);
    setReason("");
  };
  return (
    <Space.Compact style={{ width: "100%" }}>
      <Input value={reason} disabled={!editable} maxLength={200} placeholder={`变更理由：${placeholder}`} onChange={(event) => setReason(event.target.value)} onPressEnter={() => void submit()} />
      <Button type="primary" disabled={!editable || !reason.trim()} loading={loading} onClick={() => void submit()}>
        {buttonText}
      </Button>
    </Space.Compact>
  );
}

function RollbackModal({
  row,
  loading,
  onClose,
  onSubmit,
}: {
  row: QqSnapshot["audit"][number];
  loading: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [form] = Form.useForm<{ reason: string }>();
  return (
    <Modal open title={`恢复到 ${timeText(row.createdAt)} 变更之前`} confirmLoading={loading} okText="确认回滚" onCancel={onClose} onOk={() => form.submit()}>
      <Alert type="warning" showIcon style={{ marginBottom: 16 }} message="回滚会立即改变 QQ 机器人的运行配置，并新增一条审计记录。" />
      <Form form={form} layout="vertical" onFinish={(value) => onSubmit(value.reason.trim())}>
        <Form.Item name="reason" label="回滚理由" rules={[{ required: true, whitespace: true, message: "请填写回滚理由" }, { max: 200 }] }>
          <Input.TextArea rows={3} maxLength={200} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function showApiError(show: (options: { content: string; duration: number }) => void, error: Error) {
  const apiError = error as ApiError;
  show({
    content: apiError.requestId
      ? `${apiError.message}（${apiError.code}，请求 ${apiError.requestId}）`
      : apiError.message,
    duration: 8,
  });
}
